import { useEffect, useRef } from 'react';

export interface UseSleepDetectionAlphaHook {
  /** Whether sleep detection is active */
  isActive: boolean;
}

export interface UseSleepDetectionAlphaOptions {
  /** Whether to enable sleep detection (default: true) */
  enabled?: boolean;
  /** Callback to execute when wake from sleep is detected */
  onWakeFromSleep: () => void;
  /** Minimum time in milliseconds to consider as sleep (default: 30 seconds) */
  sleepThresholdMs?: number;
}

/**
 * Hook to detect when the computer comes back from sleep and execute a callback.
 * Uses multiple detection methods for reliability:
 * 1. Page Visibility API - detects when page becomes visible after being hidden
 * 2. Window focus events - detects when window regains focus
 * 3. Time-based detection - measures time gaps to detect sleep
 * @param options - Configuration options for sleep detection behavior.
 */
export const useSleepDetectionAlpha = (options: UseSleepDetectionAlphaOptions): UseSleepDetectionAlphaHook => {
  const { onWakeFromSleep, sleepThresholdMs = 30000, enabled = true } = options;
  const lastActiveTimeRef = useRef<number>(Date.now());
  const isActiveRef = useRef<boolean>(enabled);
  /** Callers pass inline arrows; the listeners must not re-register per render. */
  const onWakeFromSleepRef = useRef(onWakeFromSleep);
  onWakeFromSleepRef.current = onWakeFromSleep;
  useEffect(() => {
    if (!enabled) {
      isActiveRef.current = false;
      return;
    }
    isActiveRef.current = true;
    lastActiveTimeRef.current = Date.now();
    /** Update last active time on user activity */
    const updateLastActiveTime = (): void => {
      lastActiveTimeRef.current = Date.now();
    };
    /** Check if we've been asleep based on time gap */
    const checkForSleep = (): void => {
      const now = Date.now();
      const timeSinceLastActive = now - lastActiveTimeRef.current;
      if (timeSinceLastActive >= sleepThresholdMs) onWakeFromSleepRef.current();
      lastActiveTimeRef.current = now;
    };
    /** Handle page visibility change */
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') checkForSleep();
      else updateLastActiveTime();
    };
    /** Handle window focus */
    const handleFocus = (): void => {
      checkForSleep();
    };
    /** Handle window blur */
    const handleBlur = (): void => {
      updateLastActiveTime();
    };
    /** Periodic check for sleep detection */
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        const now = Date.now();
        const timeSinceLastActive = now - lastActiveTimeRef.current;
        /** If significant time has passed, we might have been sleeping */
        if (timeSinceLastActive >= sleepThresholdMs) onWakeFromSleepRef.current();
        lastActiveTimeRef.current = now;
      }
    }, 5000); // Check every 5 seconds
    /** Add event listeners */
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    /** Activity events to update last active time */
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'] as const;
    activityEvents.forEach(event => {
      document.addEventListener(event, updateLastActiveTime, { passive: true });
    });
    /** Cleanup function */
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateLastActiveTime);
      });
      isActiveRef.current = false;
    };
  }, [sleepThresholdMs, enabled]);
  return {
    isActive: isActiveRef.current
  };
};
